/**
 * SegmentedControl Component
 * Pill-shaped multi-option toggle selector
 */

import React from "react";
import { HStack, Button, Icon } from "@chakra-ui/react";

export type SegmentedControlSize = "sm" | "md";
export type SegmentedControlVariant = "filled" | "outline";

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ElementType;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: SegmentedControlSize;
  variant?: SegmentedControlVariant;
  disabled?: boolean;
}

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  size = "sm",
  variant = "filled",
  disabled = false,
}: SegmentedControlProps<T>) {
  const containerStyles = variant === "filled"
    ? { bg: "brand.50", borderWidth: 0 }
    : { bg: "transparent", borderWidth: "1px", borderColor: "gray.200" };

  return (
    <HStack
      spacing={1}
      p={1}
      borderRadius="full"
      display="inline-flex"
      {...containerStyles}
    >
      {options.map((option) => {
        const isSelected = value === option.value;

        return (
          <Button
            key={option.value}
            size={size}
            onClick={() => onChange(option.value)}
            isDisabled={disabled}
            bg={isSelected ? "brand.500" : "transparent"}
            color={isSelected ? "white" : "gray.500"}
            borderRadius="full"
            fontWeight="medium"
            px={3}
            minW="auto"
            h={size === "sm" ? "32px" : "36px"}
            _hover={{
              bg: isSelected ? "brand.600" : "gray.200",
            }}
            _active={{
              bg: isSelected ? "brand.700" : "gray.300",
            }}
            leftIcon={option.icon ? <Icon as={option.icon} boxSize={4} /> : undefined}
          >
            {option.label}
          </Button>
        );
      })}
    </HStack>
  );
}
